import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Divider,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
  useTheme
} from '@mui/material';
import MuiGrid from '../../components/MuiGrid';
import PersonIcon from '@mui/icons-material/Person';
import Avatar from '@mui/material/Avatar';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StopIcon from '@mui/icons-material/Stop';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { styled } from '@mui/material/styles';
import { supabase } from '../../services/supabase';
import { LiveGame, LiveGamePlayer, Player, RoleType, WinnerType } from '../../types/models';

// Similar to PlayerCircle component but adapted for live games
const PlayerSeat = styled(Box, {
  shouldForwardProp: (prop) => 
    prop !== 'isKilled' && 
    prop !== 'role' && 
    prop !== 'isActive' && 
    prop !== 'hasRole'
})<{ 
  isKilled: boolean; 
  role: RoleType | null; 
  isActive: boolean;
  hasRole: boolean;
}>(({ isKilled, role, isActive, hasRole }) => {
  let roleColor = '#bdbdbd'; // Default gray
  
  if (hasRole) {
    switch (role) {
      case 'CITIZEN':
        roleColor = '#e57373'; // Light Red
        break;
      case 'SHERIFF':
        roleColor = '#c62828'; // Dark Red
        break;
      case 'MAFIA':
        roleColor = '#424242'; // Dark Gray
        break;
      case 'DON':
        roleColor = '#212121'; // Almost Black
        break;
    }
  }
  
  return {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    borderRadius: '50%',
    width: 80,
    height: 80,
    backgroundColor: hasRole ? roleColor : 'rgba(0, 0, 0, 0.12)',
    opacity: isKilled ? 0.6 : 1,
    border: isActive ? '3px solid #4caf50' : 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    '&:hover': {
      transform: 'scale(1.05)',
    }
  };
});

const LiveGameManagement: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [gameData, setGameData] = useState<LiveGame | null>(null);
  const [gamePlayers, setGamePlayers] = useState<LiveGamePlayer[]>([]);
  const [allPlayers, setAllPlayers] = useState<Record<string, Player>>({});
  const [currentDay, setCurrentDay] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  
  // We no longer need role dialog state as roles are cycled by clicking on player cards
  
  // Kill player state
  
  // End game state
  const [endGameDialogOpen, setEndGameDialogOpen] = useState(false);
  const [selectedWinner, setSelectedWinner] = useState<WinnerType | ''>('');
  
  // Day management state
  // Day state is now handled directly without dialog
  
  useEffect(() => {
    if (gameId) {
      fetchGameData(gameId);
    }
  }, [gameId]);
  
  const fetchGameData = async (id: string) => {
    setLoading(true);
    try {
      // Fetch game data
      const { data: gameData, error: gameError } = await supabase
        .from('live_games')
        .select(`
          *,
          league:league_id (
            id,
            name
          ),
          judge:judge_id (
            id,
            nickname,
            full_name
          )
        `)
        .eq('id', id)
        .single();

      if (gameError) throw gameError;
      
      // Fetch players in this game
      const { data: playersData, error: playersError } = await supabase
        .from('live_game_players')
        .select('*')
        .eq('live_game_id', id)
        .order('seat_number');
        
      if (playersError) throw playersError;
      
      if (!playersData || playersData.length === 0) {
        navigate(`/admin/live-game-setup/${id}`);
        return;
      }
      
      // Set all players to have CITIZEN role by default if not already assigned
      for (const player of playersData) {
        if (!player.role) {
          // Update each player individually with the CITIZEN role
          const { error: updateError } = await supabase
            .from('live_game_players')
            .update({ role: 'CITIZEN' })
            .eq('id', player.id);
            
          if (updateError) throw updateError;
          
          // Update in local data
          player.role = 'CITIZEN';
        }
      }
      
      // Fetch all player details
      const playerIds = playersData.map(p => p.player_id);
      const { data: playerDetails, error: playerDetailsError } = await supabase
        .from('players')
        .select('*')
        .in('id', playerIds);
        
      if (playerDetailsError) throw playerDetailsError;
      
      // Create a lookup object for players
      const playersLookup: Record<string, Player> = {};
      playerDetails?.forEach(player => {
        playersLookup[player.id] = player;
      });
      
      setGameData(gameData as LiveGame);
      setGamePlayers(playersData as LiveGamePlayer[]);
      setAllPlayers(playersLookup);
      setCurrentDay(gameData.current_day || 0);
    } catch (error: any) {
      console.error('Error fetching game data:', error.message);
      showSnackbar('Failed to load game data', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  // Role cycling handler
  const cyclePlayerRole = async (playerId: string) => {
    if (isGameOver) return;
    
    const player = gamePlayers.find(p => p.player_id === playerId);
    if (!player) return;
    
    const currentRole = player.role;
    let newRole: RoleType = 'CITIZEN';
    
    // Cycle through roles: CITIZEN -> SHERIFF -> MAFIA -> DON -> CITIZEN
    switch (currentRole) {
      case 'CITIZEN':
        newRole = 'SHERIFF';
        break;
      case 'SHERIFF':
        newRole = 'MAFIA';
        break;
      case 'MAFIA':
        newRole = 'DON';
        break;
      case 'DON':
      default:
        newRole = 'CITIZEN';
        break;
    }
    
    // Update local state immediately for responsive UI
    const updatedPlayers = gamePlayers.map(p => 
      p.player_id === playerId
        ? { ...p, role: newRole }
        : p
    );
    setGamePlayers(updatedPlayers);
    
    try {
      const { error } = await supabase
        .from('live_game_players')
        .update({ role: newRole })
        .eq('live_game_id', gameId as string)
        .eq('player_id', playerId);
        
      if (error) {
        // Revert local state on error
        setGamePlayers(gamePlayers);
        throw error;
      }
      
      showSnackbar(`Role changed to ${getRoleDisplay(newRole)}`, 'success');
    } catch (error: any) {
      console.error('Error changing role:', error.message);
      showSnackbar(`Error: ${error.message}`, 'error');
    }
  };
  
  // Direct kill player without dialog
  const handleKillPlayerDirect = async (playerId: string) => {
    // Update local state immediately for responsive UI
    const updatedPlayers = gamePlayers.map(player => 
      player.player_id === playerId
        ? { ...player, is_killed: true, killed_day: currentDay }
        : player
    );
    setGamePlayers(updatedPlayers);
    
    try {
      const { error } = await supabase
        .from('live_game_players')
        .update({ 
          is_killed: true,
          killed_day: currentDay
        })
        .eq('live_game_id', gameId as string)
        .eq('player_id', playerId);
        
      if (error) {
        // Revert local state on error
        setGamePlayers(gamePlayers);
        throw error;
      }
      
      showSnackbar('Player killed', 'success');
    } catch (error: any) {
      console.error('Error killing player:', error.message);
      showSnackbar(`Error: ${error.message}`, 'error');
    }
  };
  
  // Revive player function
  const handleRevivePlayer = async (playerId: string) => {
    // Update local state immediately for responsive UI
    const updatedPlayers = gamePlayers.map(player => 
      player.player_id === playerId
        ? { ...player, is_killed: false, killed_day: null }
        : player
    );
    setGamePlayers(updatedPlayers);
    
    try {
      const { error } = await supabase
        .from('live_game_players')
        .update({ 
          is_killed: false,
          killed_day: null
        })
        .eq('live_game_id', gameId as string)
        .eq('player_id', playerId);
        
      if (error) {
        // Revert local state on error
        setGamePlayers(gamePlayers);
        throw error;
      }
      
      showSnackbar('Player revived', 'success');
    } catch (error: any) {
      console.error('Error reviving player:', error.message);
      showSnackbar(`Error: ${error.message}`, 'error');
    }
  };
  
  // Toggle sheriff check
  const handleToggleSheriffCheck = async (playerId: string) => {
    // Find the player
    const player = gamePlayers.find(p => p.player_id === playerId);
    if (!player) return;
    
    // Toggle the sheriff_checked value
    const newValue = !player.sheriff_checked;
    
    // Update local state immediately for responsive UI
    const updatedPlayers = gamePlayers.map(p => 
      p.player_id === playerId
        ? { ...p, sheriff_checked: newValue }
        : p
    );
    setGamePlayers(updatedPlayers);
    
    try {
      const { error } = await supabase
        .from('live_game_players')
        .update({ sheriff_checked: newValue } as any)
        .eq('live_game_id', gameId as string)
        .eq('player_id', playerId);
        
      if (error) {
        // Revert local state on error
        setGamePlayers(gamePlayers);
        throw error;
      }
      
      showSnackbar(`Sheriff check ${newValue ? 'added' : 'removed'}`, 'success');
    } catch (error: any) {
      console.error('Error toggling sheriff check:', error.message);
      showSnackbar(`Error: ${error.message}`, 'error');
    }
  };
  
  // Toggle don check
  const handleToggleDonCheck = async (playerId: string) => {
    // Find the player
    const player = gamePlayers.find(p => p.player_id === playerId);
    if (!player) return;
    
    // Toggle the don_checked value
    const newValue = !player.don_checked;
    
    // Update local state immediately for responsive UI
    const updatedPlayers = gamePlayers.map(p => 
      p.player_id === playerId
        ? { ...p, don_checked: newValue }
        : p
    );
    setGamePlayers(updatedPlayers);
    
    try {
      const { error } = await supabase
        .from('live_game_players')
        .update({ don_checked: newValue } as any)
        .eq('live_game_id', gameId as string)
        .eq('player_id', playerId);
        
      if (error) {
        // Revert local state on error
        setGamePlayers(gamePlayers);
        throw error;
      }
      
      showSnackbar(`Don check ${newValue ? 'added' : 'removed'}`, 'success');
    } catch (error: any) {
      console.error('Error toggling don check:', error.message);
      showSnackbar(`Error: ${error.message}`, 'error');
    }
  };
  
  // End game handlers
  const openEndGameDialog = () => {
    setSelectedWinner('');
    setEndGameDialogOpen(true);
  };
  
  const handleEndGame = async () => {
    if (!selectedWinner) {
      showSnackbar('Please select a winner', 'error');
      return;
    }
    
    try {
      const { error } = await supabase
        .from('live_games')
        .update({ 
          status: 'COMPLETED',
          winner: selectedWinner
        })
        .eq('id', gameId as string);
        
      if (error) throw error;
      
      // Update local state
      if (gameData) {
        setGameData({
          ...gameData,
          status: 'COMPLETED',
          winner: selectedWinner as WinnerType
        });
      }
      
      showSnackbar('Game ended successfully', 'success');
      setEndGameDialogOpen(false);
      
      // Redirect to live games admin after a short delay
      setTimeout(() => {
        navigate('/admin', { state: { refresh: true } });
      }, 1500);
    } catch (error: any) {
      console.error('Error ending game:', error.message);
      showSnackbar(`Error: ${error.message}`, 'error');
    }
  };
  
  // Day management handlers
  const handleNextDay = async () => {
    const nextDay = currentDay + 1;
    
    // Update local state immediately for responsive UI
    if (gameData) {
      setGameData({
        ...gameData,
        current_day: nextDay
      });
    }
    setCurrentDay(nextDay);
    
    try {
      const { error } = await supabase
        .from('live_games')
        .update({ current_day: nextDay })
        .eq('id', gameId as string);
        
      if (error) {
        // Revert local state on error
        if (gameData) {
          setGameData({
            ...gameData,
            current_day: currentDay
          });
        }
        setCurrentDay(currentDay);
        throw error;
      }
      
      const round = Math.floor(nextDay / 2);
      const isDay = nextDay % 2 === 0;
      showSnackbar(`Advanced to Round ${round}, ${isDay ? 'Day' : 'Night'}`, 'success');
    } catch (error: any) {
      console.error('Error advancing day:', error.message);
      showSnackbar(`Error: ${error.message}`, 'error');
    }
  };
  
  const handlePrevDay = async () => {
    if (currentDay <= 0) {
      showSnackbar('Already at day 0', 'error');
      return;
    }
    
    const prevDay = currentDay - 1;
    
    // Update local state immediately for responsive UI
    if (gameData) {
      setGameData({
        ...gameData,
        current_day: prevDay
      });
    }
    setCurrentDay(prevDay);
    
    try {
      const { error } = await supabase
        .from('live_games')
        .update({ current_day: prevDay })
        .eq('id', gameId as string);
        
      if (error) {
        // Revert local state on error
        if (gameData) {
          setGameData({
            ...gameData,
            current_day: currentDay
          });
        }
        setCurrentDay(currentDay);
        throw error;
      }
      
      const round = Math.floor(prevDay / 2);
      const isDay = prevDay % 2 === 0;
      showSnackbar(`Returned to Round ${round}, ${isDay ? 'Day' : 'Night'}`, 'success');
    } catch (error: any) {
      console.error('Error decreasing day:', error.message);
      showSnackbar(`Error: ${error.message}`, 'error');
    }
  };
  
  const getRoleDisplay = (role?: RoleType | null) => {
    if (!role) return 'Not Assigned';
    
    switch (role) {
      case 'CITIZEN': return 'Citizen';
      case 'SHERIFF': return 'Sheriff';
      case 'MAFIA': return 'Mafia';
      case 'DON': return 'Don';
      default: return role;
    }
  };
  
  const getRoleColor = (role?: RoleType | null) => {
    if (!role) return 'default';
    
    switch (role) {
      case 'CITIZEN': return 'error';
      case 'SHERIFF': return 'error';
      case 'MAFIA': return 'info';
      case 'DON': return 'info';
      default: return 'default';
    }
  };
  
  const getGameStatusText = () => {
    if (!gameData) return '';
    
    switch (gameData.status) {
      case 'PENDING': return 'Pending';
      case 'ACTIVE': {
        const round = Math.floor(currentDay / 2);
        const isDay = currentDay % 2 === 0;
        return `Active - Round ${round}, ${isDay ? 'Day' : 'Night'}`;
      }
      case 'COMPLETED': return `Completed - ${gameData.winner} won`;
      case 'CANCELLED': return 'Cancelled';
      default: return gameData.status;
    }
  };
  
  const getGameStatusColor = () => {
    if (!gameData) return 'default';
    
    switch (gameData.status) {
      case 'PENDING': return 'default';
      case 'ACTIVE': return 'success';
      case 'COMPLETED': return 'info';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!gameData) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h5" color="error">
          Game not found or you do not have access to this game.
        </Typography>
        <Button 
          variant="contained" 
          sx={{ mt: 2 }}
          onClick={() => navigate('/admin')}
        >
          Return to Admin Dashboard
        </Button>
      </Box>
    );
  }
  
  // Determine if game can be started (all players have roles assigned)
  const allPlayersHaveRoles = gamePlayers.every(player => player.role);
  const isGameActive = gameData.status === 'ACTIVE';
  const isGameOver = gameData.status === 'COMPLETED' || gameData.status === 'CANCELLED';
  
  return (
    <Box sx={{ p: isMobile ? 1.5 : 3 }}>
      {/* Compact Header for Mobile */}
      <Box sx={{ mb: isMobile ? 2 : 4 }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          flexWrap: 'wrap',
          gap: 1
        }}>
          <Typography variant={isMobile ? "h6" : "h4"} component="h1" sx={{ fontWeight: 'bold' }}>
            {isMobile ? 'Live Game' : 'Live Game Management'}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate('/admin')}
            size={isMobile ? "small" : "medium"}
            startIcon={isMobile ? undefined : <ArrowBackIcon />}
          >
            {isMobile ? 'Back' : 'Return to Dashboard'}
          </Button>
        </Box>

        {/* Game Info - Compact on Mobile */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1
        }}>
          <Typography variant={isMobile ? "body2" : "h6"} color="text.secondary">
            {isMobile
              ? `Game ${gameData.number_in_session}`
              : `${gameData.league?.name} - Session ${gameData.session_num}, Game ${gameData.number_in_session}`
            }
          </Typography>
          <Chip
            label={getGameStatusText()}
            color={getGameStatusColor()}
            size="small"
          />
        </Box>

        {!isMobile && (
          <Typography variant="body1">
            {`Judge: ${gameData.judge?.nickname}`}
          </Typography>
        )}

        {!isMobile && gameData.stream_key && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Stream Key: {gameData.stream_key}
          </Typography>
        )}

        <Box sx={{ mt: 1.5 }}>
          <TextField
            label="Overlay Text"
            variant="outlined"
            size="small"
            value={gameData.overlay_text || ''}
            onChange={(e) => {
              // Update local state immediately for responsive UI
              if (gameData) {
                setGameData({
                  ...gameData,
                  overlay_text: e.target.value
                });
              }

              // Use a debounce function to delay API updates
              if (window.overlayTextTimeout) {
                clearTimeout(window.overlayTextTimeout);
              }

              window.overlayTextTimeout = setTimeout(async () => {
                try {
                  const newText = e.target.value;
                  // First update the record
                  const { error } = await supabase
                    .from('live_games')
                    .update({
                      overlay_text: newText,
                      updated_at: new Date().toISOString() // Force update timestamp
                    })
                    .eq('id', gameId as string);

                  if (error) throw error;

                  // Then broadcast the change through realtime
                  await supabase.channel('database_changes')
                    .send({
                      type: 'broadcast',
                      event: 'overlay_text_changed',
                      payload: { gameId, text: newText }
                    });

                } catch (error: any) {
                  console.error('Error updating overlay text:', error.message);
                  showSnackbar(`Error: ${error.message}`, 'error');
                }
              }, 500); // Reduced debounce delay
            }}
            fullWidth
            placeholder="Add text to appear on overlay"
          />
        </Box>
      </Box>
      
      {/* Game Actions - Fixed at bottom on mobile for easy access */}
      {isMobile ? (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            p: 1.5,
            zIndex: 1000,
            borderRadius: 0,
            display: 'flex',
            justifyContent: 'space-around',
            gap: 1,
            boxShadow: '0 -4px 20px rgba(0,0,0,0.3)'
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handlePrevDay}
            disabled={isGameOver || currentDay <= 0}
            sx={{ minWidth: 60, minHeight: 48, flex: 1 }}
          >
            <ArrowBackIcon />
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNextDay}
            disabled={isGameOver}
            sx={{ minWidth: 60, minHeight: 48, flex: 1 }}
          >
            <ArrowForwardIcon />
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={openEndGameDialog}
            disabled={isGameOver}
            sx={{ minWidth: 60, minHeight: 48, flex: 1 }}
          >
            <StopIcon />
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => window.open(`/overlay/${gameId}`, '_blank')}
            sx={{ minWidth: 60, minHeight: 48, flex: 1 }}
          >
            <OpenInNewIcon />
          </Button>
        </Paper>
      ) : (
        <MuiGrid container spacing={3}>
          {/* Game Actions */}
          <MuiGrid item xs={12}>
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h6" sx={{ mr: 2 }}>
                  Game Actions:
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handlePrevDay}
                  disabled={isGameOver || currentDay <= 0}
                  size="medium"
                  startIcon={<ArrowBackIcon />}
                >
                  Previous
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNextDay}
                  disabled={isGameOver}
                  size="medium"
                  startIcon={<ArrowForwardIcon />}
                >
                  Next
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={openEndGameDialog}
                  disabled={isGameOver}
                  size="medium"
                  startIcon={<StopIcon />}
                >
                  End Game
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => window.open(`/overlay/${gameId}`, '_blank')}
                  size="medium"
                  startIcon={<OpenInNewIcon />}
                >
                  Open Overlay
                </Button>
              </Box>
            </Paper>
          </MuiGrid>
        </MuiGrid>
      )}
        
      {/* Player Grid - No Paper wrapper on mobile */}
      <Box sx={{ mb: isMobile ? 10 : 0 }}> {/* Bottom margin for fixed action bar on mobile */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          px: isMobile ? 0.5 : 0
        }}>
          <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 'bold' }}>
            Players
          </Typography>
          <Chip
            label={`${gamePlayers.filter(p => !p.is_killed).length} / ${gamePlayers.length} alive`}
            size="small"
            color="default"
          />
        </Box>

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : isTablet ? 'repeat(3, 1fr)' : 'repeat(5, 1fr)',
          gap: isMobile ? 1 : 2
        }}>
          {gamePlayers.map((player) => {
            const playerDetails = allPlayers[player.player_id];
            const isKilled = player.is_killed;

            // Get role background color
            const getRoleBgColor = () => {
              if (!player.role) return 'rgba(158, 158, 158, 0.2)';
              switch (player.role) {
                case 'CITIZEN': return 'rgba(244, 67, 54, 0.15)';
                case 'SHERIFF': return 'rgba(183, 28, 28, 0.2)';
                case 'MAFIA': return 'rgba(33, 33, 33, 0.3)';
                case 'DON': return 'rgba(0, 0, 0, 0.4)';
                default: return 'rgba(158, 158, 158, 0.2)';
              }
            };

            return (
              <Box
                key={player.id}
                sx={{
                  borderRadius: 2,
                  border: isKilled ? '2px solid rgba(244, 67, 54, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                  bgcolor: getRoleBgColor(),
                  opacity: isKilled ? 0.6 : 1,
                  overflow: 'hidden'
                }}
              >
                {/* Player Header - Tappable to change role */}
                <Box
                  onClick={() => !isGameOver && cyclePlayerRole(player.player_id)}
                  sx={{
                    p: isMobile ? 1.5 : 2,
                    cursor: isGameOver ? 'default' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    '&:active': !isGameOver ? { bgcolor: 'rgba(255,255,255,0.1)' } : {}
                  }}
                >
                  {/* Seat Number Badge */}
                  <Box
                    sx={{
                      width: isMobile ? 32 : 28,
                      height: isMobile ? 32 : 28,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: isMobile ? '1rem' : '0.85rem',
                      fontWeight: 'bold',
                      color: 'white',
                      flexShrink: 0,
                      bgcolor: (() => {
                        if (!player.role) return '#9e9e9e';
                        switch (player.role) {
                          case 'CITIZEN': return '#f44336';
                          case 'SHERIFF': return '#b71c1c';
                          case 'MAFIA': return '#424242';
                          case 'DON': return '#000000';
                          default: return '#9e9e9e';
                        }
                      })(),
                    }}
                  >
                    {player.seat_number}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant={isMobile ? "body1" : "subtitle2"}
                      sx={{
                        fontWeight: 'bold',
                        textDecoration: isKilled ? 'line-through' : 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {playerDetails?.nickname || `P${player.seat_number}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getRoleDisplay(player.role)}
                    </Typography>
                  </Box>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ p: isMobile ? 1 : 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {/* Kill/Revive Button */}
                  {!isKilled ? (
                    <Button
                      size={isMobile ? "medium" : "small"}
                      color="error"
                      onClick={() => handleKillPlayerDirect(player.player_id)}
                      disabled={isGameOver}
                      variant="contained"
                      fullWidth
                      sx={{ minHeight: isMobile ? 44 : 36, fontWeight: 'bold' }}
                    >
                      KILL
                    </Button>
                  ) : (
                    <Button
                      size={isMobile ? "medium" : "small"}
                      color="success"
                      onClick={() => handleRevivePlayer(player.player_id)}
                      disabled={isGameOver}
                      variant="contained"
                      fullWidth
                      sx={{ minHeight: isMobile ? 44 : 36, fontWeight: 'bold' }}
                    >
                      REVIVE
                    </Button>
                  )}

                  {/* Check Buttons Row */}
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button
                      size="small"
                      onClick={() => handleToggleSheriffCheck(player.player_id)}
                      disabled={isGameOver}
                      variant={player.sheriff_checked ? "contained" : "outlined"}
                      color="warning"
                      sx={{
                        flex: 1,
                        minHeight: isMobile ? 40 : 32,
                        fontSize: isMobile ? '0.75rem' : '0.7rem',
                        px: 0.5
                      }}
                    >
                      {isMobile ? '🔍' : 'Sheriff'}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handleToggleDonCheck(player.player_id)}
                      disabled={isGameOver}
                      variant={player.don_checked ? "contained" : "outlined"}
                      color="info"
                      sx={{
                        flex: 1,
                        minHeight: isMobile ? 40 : 32,
                        fontSize: isMobile ? '0.75rem' : '0.7rem',
                        px: 0.5
                      }}
                    >
                      {isMobile ? '🎭' : 'Don'}
                    </Button>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      
      {/* Role assignment is now handled by clicking on player cards */}
      
      
      {/* End Game Dialog */}
      <Dialog open={endGameDialogOpen} onClose={() => setEndGameDialogOpen(false)}>
        <DialogTitle>End Game</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Who won this game?
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="winner-select-label">Winner</InputLabel>
              <Select
                labelId="winner-select-label"
                value={selectedWinner}
                label="Winner"
                onChange={(e) => setSelectedWinner(e.target.value as WinnerType)}
              >
                <MenuItem value="">
                  <em>Select winner</em>
                </MenuItem>
                <MenuItem value="CIVILIANS">Civilians</MenuItem>
                <MenuItem value="MAFIA">Mafia</MenuItem>
                <MenuItem value="TIE">Tie</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndGameDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleEndGame} 
            variant="contained" 
            color="primary"
            disabled={!selectedWinner}
          >
            End Game
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Day Management Dialog removed */}
      
      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={5000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LiveGameManagement;